/** @jsxRuntime classic  */
/** @jsx jsx */
import { Component, ReactNode } from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { css, jsx } from '@emotion/core';
import { refractor, RefractorElement, Text } from 'refractor/lib/all';

import { CodeSegment } from '../types';
import astToReact from '../utils/ast-to-react';
import { NO_HIGHLIGHT } from '../utils/languages';
import wrapLines from '../utils/wrap-lines';

import * as style from './prism-bitbucket.css';

const defaultThemeCss = css(style);

type SyntaxHighlightingState = {
  highlightedCode?: Array<(RefractorElement | Text)[]>;
};

// A conditional type that ties a source input type to a callback argument type.
//
// There are two cases where we'll want to highlight syntax. One is in files or
// file excerpts. In such cases, we want the rendering callback to accept a
// single set of highlighted lines. The second is in diffs. For diffs, we need
// to accept both the before and after source files, and we'll pass two
// highlighted versions to the callback. This type expresses this constraint by
// tying the output (callback) type to the input type.
type SyntaxHighlightingOutput<Input extends string | [string, string]> =
  Input extends string ? CodeSegment[] : [CodeSegment[], CodeSegment[]];

export type SyntaxHighlightingProps<
  Input extends string | [string, string],
  Output extends SyntaxHighlightingOutput<Input>
> = {
  /** A function that accepts syntax-highlighted code as an argument (in the form of an
      array of React Nodes) and returns a value to be rendered by React */
  children?: (highlightedCode: Output) => ReactNode;
  /** The language that the provided source code is written in (see: src/utils/get-language.js) */
  language?: string;
  /** Either a single string containing the source to highlight, or
      a pair of strings with the "before" and "after" versions of the source */
  source: Input;
};

/*
 * A "render prop" / "function as children" component that provides
 * syntax-highlighting for source code
 */
// TODO: Replace usage of unsafe lifecycle methods
// eslint-disable-next-line react/no-unsafe
export default class SyntaxHighlighting<
  Input extends string | [string, string],
  Output extends SyntaxHighlightingOutput<Input>
> extends Component<
  SyntaxHighlightingProps<Input, Output>,
  SyntaxHighlightingState
> {
  state: SyntaxHighlightingState = {
    highlightedCode: undefined,
  };

  componentDidMount(): void {
    // Wait a tick to allow the un-highlighted code to render and paint (React will
    // often batch the DOM updates). Provides a shorter overall time-to-paint
    // at the expense of a brief flash of un-highlighted source code.
    setTimeout(this.highlightCode, 0);
  }

  /* eslint react/no-unsafe:warn */
  UNSAFE_componentWillReceiveProps(
    nextProps: SyntaxHighlightingProps<Input, Output>
  ): void {
    if (this.shouldToggleHighlight(this.props, nextProps)) {
      // clear the highlightedCode state
      this.setState({ highlightedCode: undefined });
    }
  }

  componentDidUpdate(prevProps: SyntaxHighlightingProps<Input, Output>): void {
    if (this.shouldToggleHighlight(prevProps, this.props)) {
      this.highlightCode();
    }
  }

  shouldToggleHighlight = (
    before: SyntaxHighlightingProps<Input, Output>,
    after: SyntaxHighlightingProps<Input, Output>
  ): boolean =>
    before.source !== after.source || before.language !== after.language;

  // Even though the external API accepts either a single string or a pair of
  // strings, internally we want to treat everything as a list for simplicity,
  // and convert back to a single item or a tuple on the way back to client
  // code. This function lets us always treat the input as a list.
  sourceAsList = (): Array<string> =>
    typeof this.props.source === 'string'
      ? [this.props.source]
      : // `as Array<string>` shouldn't be necessary, but `yarn typecheck` complains if it's not here.
        (this.props.source as Array<string>);

  // Just like sourceAsList abstracts over the input, this abstacts over the
  // output. It calls the provided callback with the appropriate arguments based
  // on the input type.
  callRenderCallback = (highlightedCode: CodeSegment[][]): ReactNode => {
    if (typeof this.props.source === 'string') {
      // Input was a single source file; call callback with single result
      const renderCode = this.props.children as (
        highlightedCode: SyntaxHighlightingOutput<string>
      ) => ReactNode;
      return renderCode(highlightedCode[0]);
    } else {
      // Input was a pair of source files; call callback with pair
      const renderCode = this.props.children as (
        highlightedCode: SyntaxHighlightingOutput<[string, string]>
      ) => ReactNode;
      return renderCode([highlightedCode[0], highlightedCode[1]]);
    }
  };

  highlightCode = (): void => {
    const { language } = this.props;
    let code;

    if (language && language !== NO_HIGHLIGHT) {
      code = this.sourceAsList().map(
        s => refractor.highlight(s, language).children
      );
    }

    this.setState({ highlightedCode: code });
  };

  splitIntoSegments = (source: string): CodeSegment[] => {
    return source
      .split('\n')
      .map((line, i) => <span key={`code-segment-${i}`}>{line}</span>);
  };

  render(): ReactNode {
    if (!this.props.children) {
      return null;
    }

    if (!this.state.highlightedCode) {
      // Code has not yet been highlighted; just split the source file and
      // wrap each line in a container.
      const renderedCode = this.sourceAsList().map(source =>
        this.splitIntoSegments(source)
      );
      return this.callRenderCallback(renderedCode);
    }

    // Transform the DOM node objects that highlight engine returns
    // into React Elements, for each file that we highlight.
    const reactCode = this.state.highlightedCode.map(highlighterNodes =>
      highlighterNodes.map((node, i) =>
        astToReact(node, { key: `code-segment-${i}` })
      )
    );

    const themedCode = reactCode.map(fileSegments =>
      wrapLines(fileSegments).map((el, i) => (
        /* eslint react/no-unknown-property: "warn" */
        // eslint-disable-next-line react/no-array-index-key
        <span css={defaultThemeCss} key={`code-segment-${i}`}>
          {el.props.children}
        </span>
      ))
    );

    return this.callRenderCallback(themedCode);
  }
}
